import { EventCallback } from "../../models/event/EventCallback";

/**
 * Manages custom event subscriptions and dispatching.
 * Allows multiple listeners per event and supports optional `id` suffixing.
 */
export class EventManager {
  private listeners: Record<string, Set<EventCallback<any>>> = {};
  private stateEvents: Set<string> = new Set();
  private lastPayloads: Record<string, any> = {};

  constructor() {
    this.stateEvents.add("screen:mobile");
    this.stateEvents.add("screen:tablet");
    this.stateEvents.add("screen:laptop");
    this.stateEvents.add("screen:desktop");
    this.stateEvents.add("start");
  }

  /**
   * Marks an event as stateful so the last payload is cached and replayed to new listeners.
   * Optionally seeds the initial payload.
   */
  registerStateEvent(eventName: string, initialPayload?: any): void {
    this.stateEvents.add(eventName);
    if (initialPayload !== undefined) {
      this.lastPayloads[eventName] = initialPayload;
    }
  }

  /**
   * Subscribes to an event.
   * Optionally appends an `id` to the event name for namespacing.
   *
   * @param eventName The base event name (e.g. "scroll", "update").
   * @param callback The function to call when the event is emitted.
   * @param id Optional unique identifier to scope the event (e.g. element ID).
   */
  on<T = any>(eventName: string, callback: EventCallback<T>, id?: string | null): void {
    const fullEvent = id ? `${eventName}:${id}` : eventName;

    if (!this.listeners[fullEvent]) {
      this.listeners[fullEvent] = new Set();
    }
    this.listeners[fullEvent].add(callback);
    if (this.stateEvents.has(fullEvent) && this.lastPayloads[fullEvent] !== undefined) {
      callback(this.lastPayloads[fullEvent]);
    }
  }

  /**
   * Unsubscribes from a specific event listener.
   * Must match the original `eventName`, `callback`, and optional `id`.
   *
   * @param eventName The base event name to unsubscribe from.
   * @param callback The callback function to remove.
   * @param id Optional identifier used when subscribing.
   */
  off<T = any>(eventName: string, callback: EventCallback<T>, id?: string): void {
    const fullEvent = id ? `${eventName}:${id}` : eventName;

    if (this.listeners[fullEvent]) {
      this.listeners[fullEvent].delete(callback);
    }
  }

  /**
   * Emits an event with an optional payload.
   * All matching listeners will be called.
   *
   * @param eventName The full event name (must include `id` if used).
   * @param payload Optional data passed to event listeners.
   */
  emit<T = any>(eventName: string, payload?: T): void {
    if (this.stateEvents.has(eventName)) {
      this.lastPayloads[eventName] = payload;
    }
    const set = this.listeners[eventName];
    if (!set) return;
    for (const callback of set) {
      callback(payload as T);
    }
  }

  /**
   * Subscribes to a per-object progress event.
   * @param id The object ID.
   * @param callback The callback to handle progress value.
   */
  onProgress(id: string, callback: EventCallback<number>): void {
    this.on(`progress:${id}`, callback);
  }

  /**
   * Emits a per-object progress event.
   * @param id The object ID.
   * @param value The progress value.
   */
  emitProgress(id: string, value: number): void {
    this.emit(`progress:${id}`, value);
  }

  /**
   * Subscribes to a per-object in-view event.
   * @param id The object ID.
   * @param callback The callback to handle visibility.
   */
  onInview(id: string, callback: EventCallback<boolean>): void {
    this.on(`object:inview:${id}`, callback);
  }

  /**
   * Emits a per-object in-view event.
   * @param id The object ID.
   * @param visible Whether the object is visible.
   */
  emitInview(id: string, visible: boolean): void {
    this.emit(`object:inview:${id}`, visible);
  }

  /**
   * Subscribes to the global scroll event.
   * @param callback The callback to handle scroll value.
   */
  onScroll(callback: EventCallback<number>): void {
    this.on(`scroll`, callback);
  }

  /**
   * Emits the global scroll event.
   * @param value The scroll value.
   */
  emitScroll(value: number): void {
    this.emit(`scroll`, value);
  }

  /**
   * Subscribes to the global update event.
   * @param callback The callback to handle update.
   */
  onUpdate(callback: EventCallback<void>): void {
    this.on(`update`, callback);
  }

  /**
   * Emits the global update event.
   */
  emitUpdate(): void {
    this.emit(`update`);
  }

  /**
   * Clears all listeners for a specific event.
   *
   * @param eventName The full event name (including optional `id`).
   */
  clear(eventName: string): void {
    delete this.listeners[eventName];
  }

  /**
   * Clears all registered events.
   */
  clearAll(): void {
    this.listeners = {};
  }
}
